import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { Button, Spinner } from '../../components/design-system';
import { AdminPage } from './AdminPage';
import { HostDashboard } from '../../components/admin/HostDashboard';
import { hostStats, type HostProfileRow, type HostStat, type PaymentRow } from '../../lib/hostAnalytics';
import type { Journey } from '../../lib/tripPipeline';
import type { AdminQuestion, AdminSurvey } from '../../lib/tripSurvey';

export function AdminHosts() {
  const { user } = useAuth();
  const [stats, setStats] = useState<HostStat[]>([]);
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    const [pipe, survey, hp, pay] = await Promise.all([
      supabase.rpc('admin_trip_pipeline'),
      supabase.rpc('admin_trip_survey_overview'),
      supabase.from('host_profiles').select('profile_id, city, active, consecutive_missed_trips, suspended_for_missed_trips_at'),
      supabase.from('payments').select('host_id, status, amount_cents, commission_cents, host_payout_cents, refunded_amount_cents'),
    ]);
    const failed = pipe.error ?? survey.error ?? hp.error ?? pay.error;
    if (failed) {
      console.error('AdminHosts: load failed', failed);
      setError('Couldn’t load the hosts dashboard. Please try again.');
      setLoading(false);
      return;
    }
    const hpRows = (hp.data ?? []) as {
      profile_id: string; city: string | null; active: boolean;
      consecutive_missed_trips: number | null; suspended_for_missed_trips_at: string | null;
    }[];
    const { data: names } = hpRows.length
      ? await supabase.from('profiles').select('id, full_name').in('id', hpRows.map((r) => r.profile_id))
      : { data: [] };
    const nameOf = new Map(((names ?? []) as { id: string; full_name: string | null }[]).map((p) => [p.id, p.full_name]));
    const profiles: HostProfileRow[] = hpRows.map((r) => ({
      profile_id: r.profile_id, name: nameOf.get(r.profile_id) ?? null, city: r.city, active: r.active,
      consecutive_missed_trips: r.consecutive_missed_trips ?? 0, suspended: !!r.suspended_for_missed_trips_at,
    }));
    const j = (pipe.data ?? []) as Journey[];
    const overview = survey.data as { questions: AdminQuestion[]; surveys: AdminSurvey[] } | null;
    setJourneys(j);
    setStats(hostStats(j, profiles, overview?.questions ?? [], overview?.surveys ?? [], (pay.data ?? []) as PaymentRow[]));
    setLoading(false);
  }

  useEffect(() => { if (user) void load(); }, [user]);

  return (
    <AdminPage title="Hosts" description="Every host at a glance: trips by stage, ratings, how fast they confirm and who needs a look.">
      {error && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3">
          <span className="text-xs text-crimson">{error}</span>
          <Button size="sm" variant="ghost" onClick={() => void load()}>Retry</Button>
        </div>
      )}
      {loading ? <div className="flex justify-center py-12"><Spinner /></div> : !error && <HostDashboard stats={stats} journeys={journeys} />}
    </AdminPage>
  );
}

export default AdminHosts;
